/**
 * Stage 5a — structure repair with cooldown (avoid retry-structure storm).
 */
import { isAxiosError } from 'axios'
import { ChatApiService } from './chatApiService'
import { isBackendRoomReady } from './chatReadiness'
import type { IChat } from '@/modules/chat-matrix-fast/types/IChat'
import { chatDebugLog } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

const DEFAULT_ATTEMPTS = 10
const DEFAULT_DELAY_MS = 1500

/** Max retry-structure calls per chat within a browser session window. */
const REPAIR_MAX_PER_CHAT = 3
const REPAIR_COOLDOWN_MS = 5000

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

function isRetryStructureUnavailable(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 503
}

interface RepairState {
  count: number
  lastAt: number
  inFlight: Promise<IChat | null> | null
}

const repairByChatId = new Map<string, RepairState>()

function getRepairState(chatId: number | string): RepairState {
  const key = String(chatId)
  let state = repairByChatId.get(key)
  if (!state) {
    state = { count: 0, lastAt: 0, inFlight: null }
    repairByChatId.set(key, state)
  }
  return state
}

/**
 * Debounced / cooled-down POST …/retry-structure/.
 * Returns updated chat when API succeeds, otherwise null.
 */
export async function requestStructureRepair(
  chatId: number | string,
  options?: { force?: boolean },
): Promise<IChat | null> {
  if (chatId == null || chatId === '') {
    return null
  }

  const state = getRepairState(chatId)
  if (state.inFlight) {
    return state.inFlight
  }

  const now = Date.now()
  if (!options?.force) {
    if (state.count >= REPAIR_MAX_PER_CHAT) {
      return null
    }
    if (state.lastAt && now - state.lastAt < REPAIR_COOLDOWN_MS) {
      return null
    }
  }

  state.inFlight = (async () => {
    state.count += 1
    state.lastAt = Date.now()
    chatDebugLog('requestStructureRepair → retryStructure', {
      chatId,
      attempt: state.count,
    })
    try {
      return await ChatApiService.retryStructure(chatId)
    } catch (error) {
      chatDebugLog('requestStructureRepair → failed', {
        chatId,
        error: error instanceof Error ? error.message : String(error),
      })
      if (isRetryStructureUnavailable(error)) {
        throw error
      }
      return null
    } finally {
      state.inFlight = null
    }
  })()

  return state.inFlight
}

/**
 * Re-fetch чата, пока backend не провижинит matrix_room_id (и Space для topics).
 * При transition_status=failed вызывает retry-structure с cooldown (до 3 раз).
 */
export async function pollChatUntilMatrixRoom(
  fetchChat: () => Promise<IChat>,
  options?: { attempts?: number; delayMs?: number },
): Promise<IChat> {
  const attempts = options?.attempts ?? DEFAULT_ATTEMPTS
  const delayMs = options?.delayMs ?? DEFAULT_DELAY_MS

  let chat = await fetchChat()
  if (isBackendRoomReady(chat)) {
    return chat
  }
  if (chat.transition_status === 'failed' && chat.id) {
    const repaired = await requestStructureRepair(chat.id)
    if (repaired) {
      chat = repaired
      if (isBackendRoomReady(chat)) {
        return chat
      }
    }
  }

  for (let attempt = 1; attempt < attempts; attempt += 1) {
    await sleep(delayMs)
    chat = await fetchChat()
    if (isBackendRoomReady(chat)) {
      return chat
    }
    if (chat.transition_status === 'failed' && chat.id) {
      const repaired = await requestStructureRepair(chat.id)
      if (repaired) {
        chat = repaired
        if (isBackendRoomReady(chat)) {
          return chat
        }
      }
    }
  }

  return chat
}

/** @deprecated use isChatProvisioning from chatReadiness */
export { isChatProvisioning } from './chatReadiness'
